import React, { useState, useEffect } from "react";
import { getAccessToken } from "../../services/common.service";
import { useParams, useNavigate } from "react-router-dom";
import { TLang } from "stencil-library";
import { FarmActivityCard, ScreenHeader } from "react-library";

import FarmActivitiesService, {
  TFarmActivity,
} from "../../services/farm-activities.services";
import "./calendar.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { CORE_API } from "../../constants";
import { Routes } from "../../routes";
import { t } from "../../services/locale.service";

const CalendarScreen = () => {
  const { plotId, orgId } = useParams();
  const token = getAccessToken();
  const language = localStorage.getItem("user_language") as TLang;
  const navigate = useNavigate();

  const [farmActivities, setFarmActivities] = useState<TFarmActivity[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const fetchFarmActivities = async () => {
    const newActivities = await FarmActivitiesService.getPlotAcitivities(
      plotId!,
      offset
    );

    if (newActivities.length === 0) {
      setHasMore(false); // Stop fetching when no more activities
    } else {
      setFarmActivities((prevActivities) => [
        ...prevActivities,
        ...newActivities,
      ]);
      setOffset((prevOffset) => prevOffset + newActivities.length); // Increase the offset for the next batch
    }
  };

  const handleActivityAction = async (item: TFarmActivity, action: string, selectedOption?: string) => {
    try {
      const id = item.id;
      if (!id) {
        console.error("Activity ID is missing");
        return;
      }
  
      if (!action) {
        console.error("Action type is missing");
        return;
      }
  
      let method = "";
      const body: {
        notes?: string;
        done?: boolean;
        doneDate?: string;
        rejected?: boolean;
      } = {};
  
      if (action === "delete") {
        method = "DELETE";
      } else if (action === "done") {
        method = "PATCH";
        body.notes = selectedOption;
        body.done = true;
        body.doneDate = new Date().toISOString();
      } else if (action === "NotRequired") {
        method = "PATCH";
        body.rejected = true;
      } else {
        console.error("Invalid action type:", action);
        return;
      }
  
      // Make the API call
      const res = await fetch(CORE_API + "/farm-activities/" + id, {
        method: method,
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: method !== "DELETE" ? JSON.stringify(body) : undefined,
      });
  
      if (!res.ok) {
        const errorMessage = await res.text();
        alert(`Error: ${errorMessage || "Failed to update activity"}`);
        return;
      }

      if (action === "delete") {
        setFarmActivities((prevActivities) => prevActivities.filter((activity) => activity.id !== id));
      } else {
        setFarmActivities((prevActivities) => {
          return prevActivities.map((activity) =>
            activity.id === id
              ? { ...activity, ...body }  
              : activity
          );
        });
      } 
    } catch (error) {
      console.error("An unexpected error occurred:", error);
       
    }
  };
  
 
  useEffect(() => {
    const handleBackIconPress = () => {
      handleBack();
    };

    window.addEventListener("back-icon-press", handleBackIconPress);

    return () => {
      window.removeEventListener("back-icon-press", handleBackIconPress);
    };
  }, [navigate]);

  const handleBack = () => {
    try {
      navigate(-1);  
    } catch (error) {
      console.log(error);
    }
  };


  // Effect to load initial data
  useEffect(() => {
    fetchFarmActivities();
  }, []);

  return (
    <div style={{ position: "relative"}}>
      <ScreenHeader screenName={t("cropCalendar")} />
      <div
        className="calendar-screen-container"
        id="scrollableDiv"
        style={{
          height: "92vh",
          overflowY: "scroll",
          display: "flex",
          flexDirection: "column-reverse",
          margin: "auto",
        }}
      >
        {!farmActivities?.length ? (
          <div className="calendar-screen-empty-wrapper">
            <p
              style={{
                textAlign: "center",
                fontSize: "14px",
                color: "#7c7c7c",
              }}
            >
              {t("No_activity")}
            </p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={farmActivities.length}
            next={fetchFarmActivities}
            hasMore={hasMore}
            loader={<p style={{ textAlign: "center" }}>⏳&nbsp;{t("Loading")}</p>}
            endMessage={
              <p
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  color: "#7c7c7c",
                }}
              >
                {farmActivities?.length ? t("end")! : t("No_activity")}
              </p>
            }
            style={{
              display: "flex",
              flexDirection: "column-reverse",
              overflow: "visible",
              marginBottom: "25%"
            }} // Keep the reverse scroll behavior
            scrollableTarget="scrollableDiv"
            inverse={true}
          >
            {farmActivities.map((activity, index) => (
              <div key={index}>
                <FarmActivityCard
                  language={language}
                  farmData={activity}
                  token={token}
                  whenCancelledSuccessfull={() =>
                    handleActivityAction(activity, "delete")
                  }
                  whenDoneSuccessfully={() => {
                    handleActivityAction(activity, "done");
                  }}
                  whenPopupMenuOptionClick={(optionType: string) =>
                    handleActivityAction(activity, optionType)
                  }
                />
              </div>
            ))}
          </InfiniteScroll>
        )}
      </div>
      <div
        style={{
          marginLeft: "14px",
          position: "absolute",
          bottom: "15px",
          left: "0px",
          backgroundColor: "#014751",
          borderRadius: "20px",
          padding: "0px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          width: "85%",
          zIndex: 1000
        }}
        onClick={() =>
          navigate(
            Routes.addActivity
              .replace(":plotId", plotId!)
              .replace(":orgId", orgId!)
          )
        }
      >
        <div
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p
            style={{
              color: "#fff",
              fontWeight: "bold",
              fontSize: 12,
              fontFamily: "RobotoBold",
              marginLeft: 2,
            }}
          >
            {t("addActivity")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CalendarScreen;
